(() => {
  if (customElements.get('product-swipe-gallery')) return;

  class ProductSwipeGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('[data-swipe-track]');
      this.slides = [...this.querySelectorAll('[data-swipe-slide]')];
      this.dots = [...this.querySelectorAll('[data-swipe-dot]')];
      this.previousButton = this.querySelector('[data-swipe-previous]');
      this.nextButton = this.querySelector('[data-swipe-next]');
      this.counter = this.querySelector('[data-gallery-index]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.drag = null;
      this.animationFrame = null;
      this.scrollFrame = null;
      this.scrollTimer = null;
      this.suppressClickUntil = 0;

      if (!this.track || this.slides.length < 1) return;

      this.previousButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.swipeDot))));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event));
      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.track.addEventListener('pointerup', (event) => this.onPointerEnd(event));
      this.track.addEventListener('pointercancel', (event) => this.onPointerEnd(event));
      this.track.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
      this.track.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
      this.track.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
      this.track.addEventListener('touchcancel', (event) => this.onTouchEnd(event), { passive: true });
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
      this.addEventListener('click', (event) => {
        if (Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      this.resizeObserver = new ResizeObserver(() => this.goTo(this.index, false));
      this.resizeObserver.observe(this.track);
      this.update();
      this.updateParallax();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.animationFrame);
      cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.scrollTimer);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0 || this.slides.length < 2) return;
      cancelAnimationFrame(this.animationFrame);
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScroll: this.track.scrollLeft,
        startIndex: this.index,
        moved: false,
      };
      this.track.style.scrollBehavior = 'auto';
      this.track.style.scrollSnapType = 'none';
    }

    onPointerMove(event) {
      if (!this.drag || this.drag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - this.drag.startX;
      const deltaY = event.clientY - this.drag.startY;

      if (!this.drag.moved && Math.abs(deltaY) > Math.abs(deltaX)) return;
      if (!this.drag.moved && Math.abs(deltaX) > 5) {
        this.drag.moved = true;
        this.classList.add('is-dragging');
        this.track.setPointerCapture?.(event.pointerId);
      }
      if (!this.drag.moved) return;

      if (event.cancelable) event.preventDefault();
      this.track.scrollLeft = this.drag.startScroll - deltaX;
    }

    onPointerEnd(event) {
      if (!this.drag || this.drag.pointerId !== event.pointerId) return;
      if (this.track.hasPointerCapture?.(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
      this.finishDrag(event.clientX);
    }

    onTouchStart(event) {
      if (this.slides.length < 2 || event.touches.length !== 1) return;
      const touch = event.touches[0];
      cancelAnimationFrame(this.animationFrame);
      this.drag = {
        pointerId: 'touch',
        startX: touch.clientX,
        startY: touch.clientY,
        startScroll: this.track.scrollLeft,
        startIndex: this.index,
        moved: false,
      };
      this.track.style.scrollBehavior = 'auto';
      this.track.style.scrollSnapType = 'none';
    }

    onTouchMove(event) {
      if (!this.drag || this.drag.pointerId !== 'touch' || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - this.drag.startX;
      const deltaY = touch.clientY - this.drag.startY;

      if (!this.drag.moved && Math.abs(deltaY) > Math.abs(deltaX)) return;
      if (Math.abs(deltaX) > 5) {
        this.drag.moved = true;
        this.classList.add('is-dragging');
      }
      if (!this.drag.moved) return;

      event.preventDefault();
      this.track.scrollLeft = this.drag.startScroll - deltaX;
    }

    onTouchEnd(event) {
      if (!this.drag || this.drag.pointerId !== 'touch') return;
      const touch = event.changedTouches[0];
      this.finishDrag(touch?.clientX ?? this.drag.startX);
    }

    finishDrag(clientX) {
      const deltaX = clientX - this.drag.startX;
      const moved = this.drag.moved;
      let target = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));

      if (Math.abs(deltaX) > 34) target = this.drag.startIndex + (deltaX < 0 ? 1 : -1);
      this.track.style.removeProperty('scroll-behavior');
      this.track.style.removeProperty('scroll-snap-type');
      this.classList.remove('is-dragging');
      this.drag = null;

      if (moved) this.suppressClickUntil = Date.now() + 350;
      this.goTo(target);
    }

    onScroll() {
      if (!this.scrollFrame) {
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateParallax();
          if (!this.drag) {
            const next = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
            if (next !== this.index) {
              this.index = this.clamp(next);
              this.update();
            }
          }
        });
      }

      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        if (!this.drag) this.goTo(Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1)));
      }, 90);
    }

    goTo(nextIndex, animate = true) {
      this.index = this.clamp(nextIndex);
      const destination = this.slides[this.index]?.offsetLeft || 0;
      this.track.scrollTo({
        left: destination,
        behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'auto',
      });
      this.update();
    }

    clamp(index) {
      return Math.max(0, Math.min(this.slides.length - 1, Number(index) || 0));
    }

    update() {
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.setAttribute('aria-hidden', String(!active));
        const interactiveElements = [
          ...(slide.matches('a, button, input, video') ? [slide] : []),
          ...slide.querySelectorAll('a, button, input, video'),
        ];
        interactiveElements.forEach((element) => {
          if (active) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(this.index + 1);
      if (this.previousButton) this.previousButton.disabled = this.index === 0;
      if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
    }

    updateParallax() {
      const width = Math.max(this.track.clientWidth, 1);
      this.slides.forEach((slide) => {
        const progress = Math.max(-1.25, Math.min(1.25, (slide.offsetLeft - this.track.scrollLeft) / width));
        slide.querySelector('img')?.style.setProperty('--swipe-parallax', `${(progress * -18).toFixed(2)}px`);
      });
    }
  }

  customElements.define('product-swipe-gallery', ProductSwipeGallery);
})();
